import { AddIcon } from "@chakra-ui/icons";
import { Flex, Button } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { allLedgerTypeOptions } from "../../../../../helper/transaction/getLedgerType";
import { EditTransactionForm } from "../../../../../types/order/addOrderForm";
import { EditModeLedgerTableRow } from "./EditModeLedgerTableRow";
import { TransactionOverviewPaddingX } from "./SectionTitle";
import { TransactionOverviewLedgerEntry } from "./TransactionOverviewLedgerEntry";

export function EditableLedgerSection({
  ledgers,
  updating,
}: {
  ledgers: TransactionOverviewLedgerEntry[];
  updating: boolean;
}): React.ReactElement {
  const { control, setValue } = useFormContext<EditTransactionForm>();

  const {
    fields: newLedgers,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "newLedgers",
  });

  const watchedUpdateLedgers = useWatch({
    control,
    name: "updateLedgers",
  });

  const watchedNewLedgers = useWatch({
    control,
    name: "newLedgers",
  });

  // you can't delete the last ledger on a transaction
  const isDeleteDisabled = useMemo(() => {
    const existingLedgersCount = watchedUpdateLedgers.filter(
      (ledger) => !ledger.delete,
    ).length;

    const ledgerCount = existingLedgersCount + watchedNewLedgers.length;

    return ledgerCount <= 1;
  }, [watchedUpdateLedgers, watchedNewLedgers]);

  return (
    <>
      {ledgers.map(({ ledger }, index) => {
        const deleted = watchedUpdateLedgers[index]?.delete;

        if (deleted) {
          return null;
        }

        return (
          <EditModeLedgerTableRow
            validTypes={allLedgerTypeOptions}
            name={`updateLedgers.${index}`}
            key={ledger._id}
            isDisabled={updating}
            onDelete={() =>
              setValue(`updateLedgers.${index}.delete`, true, {
                shouldDirty: true,
              })
            }
            isDeleteDisabled={isDeleteDisabled}
          />
        );
      })}

      {newLedgers.map((ledger, index) => (
        <EditModeLedgerTableRow
          name={`newLedgers.${index}`}
          validTypes={allLedgerTypeOptions}
          key={ledger.id}
          isDisabled={updating}
          onDelete={() => remove(index)}
          isDeleteDisabled={isDeleteDisabled}
        />
      ))}

      <Flex px={TransactionOverviewPaddingX} py={2}>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gray"
          leftIcon={<AddIcon />}
          onClick={() =>
            append({
              type: undefined,
              marketValue: "",
              amount: "",
              assetId: undefined,
            })
          }
        >
          Add ledger
        </Button>
      </Flex>
    </>
  );
}
